import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Users, Save, X, Loader2, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { viewer: 'Viewer', member: 'Member', manager: 'Manager', admin: 'Admin' };
const ROLE_COLOR = {
  viewer:  'bg-gray-100 text-gray-700',
  member:  'bg-green-100 text-green-800',
  manager: 'bg-blue-100 text-blue-800',
  admin:   'bg-purple-100 text-purple-800',
};

// "Admin" users are always unscoped, so we don't render assignment cells for them.
const isUnscoped = (role) => role === 'admin' || role === 'super_admin';

export default function OrgAccessTab({ currentUserId }) {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user_id being edited
  const [search, setSearch] = useState('');

  useEffect(() => { fetchMatrix(); }, []);

  const fetchMatrix = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/users/access-matrix`, { withCredentials: true });
      setMatrix(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load access matrix');
    } finally {
      setLoading(false);
    }
  };

  const visibleUsers = useMemo(() => {
    if (!matrix) return [];
    const q = search.trim().toLowerCase();
    return matrix.users.filter((u) =>
      !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [matrix, search]);

  if (loading) {
    return (
      <div className="py-16 flex justify-center" data-testid="org-access-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!matrix) return null;

  return (
    <div className="space-y-4" data-testid="org-access-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-500" />
            Org Access ({matrix.users.length} user{matrix.users.length === 1 ? '' : 's'})
            <span className="ml-auto text-xs font-normal text-gray-500">
              {matrix.orgs.length} org{matrix.orgs.length === 1 ? '' : 's'} · click a user to edit
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-3"
            data-testid="org-access-search"
          />

          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-[260px]">User</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 w-[100px]">Role</th>
                  {matrix.orgs.map((o) => (
                    <th key={o.org_id} className="px-2 py-2 text-center font-medium text-gray-600" title={o.name}>
                      <div className="truncate max-w-[120px] mx-auto">{o.name}</div>
                    </th>
                  ))}
                  <th className="px-3 py-2 w-[60px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visibleUsers.map((u) => {
                  const unscoped = isUnscoped(u.role);
                  const accessSet = new Set(u.org_access || []);
                  const isYou = u.user_id === currentUserId;
                  return (
                    <tr key={u.user_id} className="hover:bg-gray-50/60" data-testid={`access-row-${u.user_id}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 shrink-0">
                              {u.name?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {u.name || u.email}
                              {isYou && <span className="ml-1 text-[10px] text-gray-400">(You)</span>}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${ROLE_COLOR[u.role] || 'bg-gray-100 text-gray-700'}`}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      {matrix.orgs.map((o) => (
                        <td key={o.org_id} className="px-2 py-2 text-center" data-testid={`cell-${u.user_id}-${o.org_id}`}>
                          {unscoped ? (
                            <span className="text-[10px] text-purple-600 font-medium">ALL</span>
                          ) : accessSet.has(o.org_id) ? (
                            <div className="inline-flex flex-col items-center gap-0.5">
                              <Check className="w-4 h-4 text-emerald-500" />
                              {u.role === 'member' || u.role === 'viewer' ? (
                                <span className="text-[9px] text-gray-500">
                                  {(u.company_access?.[o.org_id]?.length || 0) > 0
                                    ? `${u.company_access[o.org_id].length} co.`
                                    : 'all co.'}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right">
                        {!unscoped && !isYou ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setEditing(u)}
                            data-testid={`edit-access-${u.user_id}`}
                          >
                            Edit
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {visibleUsers.length === 0 && (
                  <tr><td colSpan={matrix.orgs.length + 3} className="px-3 py-6 text-center text-gray-400 text-sm">No users match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Has access</span>
            <span className="inline-flex items-center gap-1"><span className="text-purple-600 font-medium">ALL</span> Admin (unscoped)</span>
            <span className="inline-flex items-center gap-1"><span className="text-gray-300">—</span> No access</span>
            <span className="ml-auto">
              Tip: managers see "all co." until you restrict member/viewer access to specific companies.
            </span>
          </div>
        </CardContent>
      </Card>

      {editing && (
        <EditAccessModal
          user={editing}
          orgs={matrix.orgs}
          companiesByOrg={matrix.companies_by_org}
          allUsers={matrix.users}
          callerScope={{
            unscoped: matrix.caller_unscoped,
            role: matrix.caller_role,
            org_access: matrix.caller_org_access,
            company_access: matrix.caller_company_access || {},
            delegatedEnabled: !!matrix.delegated_manager_scope_enabled,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchMatrix(); }}
        />
      )}
    </div>
  );
}


function EditAccessModal({ user, orgs, companiesByOrg, allUsers, callerScope, onClose, onSaved }) {
  const [orgAccess, setOrgAccess] = useState(new Set(user.org_access || []));
  const [companyAccess, setCompanyAccess] = useState({ ...(user.company_access || {}) });
  const [saving, setSaving] = useState(false);
  const [copySource, setCopySource] = useState('');

  const isMemberOrViewer = user.role === 'member' || user.role === 'viewer';
  const isDelegated = !callerScope.unscoped && callerScope.role === 'manager';
  // Set of orgs the caller can grant. Admins → all orgs visible. Managers → only their assigned ones.
  const callerOrgSet = useMemo(
    () => callerScope.unscoped ? new Set(orgs.map((o) => o.org_id)) : new Set(callerScope.org_access || []),
    [callerScope, orgs]
  );
  // For Managers: companies they can grant *per org*. Null/empty list ⇒ all companies in that org.
  const callerCompanyByOrg = callerScope.company_access || {};
  const canGrantCompany = (org_id, name) => {
    if (callerScope.unscoped) return true;
    const allowed = callerCompanyByOrg[org_id];
    if (!Array.isArray(allowed) || allowed.length === 0) return true;
    return allowed.includes(name);
  };
  // Existing access that the caller is "outside of" — must be preserved (union) and rendered as locked
  const lockedOrgs = useMemo(() => {
    if (callerScope.unscoped) return new Set();
    return new Set((user.org_access || []).filter((o) => !callerOrgSet.has(o)));
  }, [user.org_access, callerScope.unscoped, callerOrgSet]);

  // Candidates: any non-admin user other than the one being edited.
  // Admins are excluded because their access is implicit (no list to clone).
  const copyCandidates = (allUsers || [])
    .filter((u) => u.user_id !== user.user_id && !['admin', 'super_admin'].includes(u.role))
    .filter((u) => Array.isArray(u.org_access) && u.org_access.length > 0)
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));

  const copyFrom = (sourceId) => {
    const src = copyCandidates.find((u) => u.user_id === sourceId);
    if (!src) return;
    setOrgAccess(new Set(src.org_access || []));
    // For Member/Viewer targets, also clone company restrictions. For Manager
    // targets, the field is ignored anyway — clear it for cleanliness.
    setCompanyAccess(isMemberOrViewer ? { ...(src.company_access || {}) } : {});
    setCopySource('');
    toast.success(`Copied access from ${src.name || src.email}`);
  };

  const toggleOrg = (org_id) => {
    if (lockedOrgs.has(org_id)) {
      toast.info('This org was granted by an Admin and is outside your scope. Ask an Admin to revoke it.');
      return;
    }
    if (!callerScope.unscoped && !callerOrgSet.has(org_id)) {
      toast.info('Outside your scope — ask an Admin to grant access to this org first.');
      return;
    }
    const next = new Set(orgAccess);
    if (next.has(org_id)) {
      next.delete(org_id);
      // Drop company restrictions for this org too
      const ca = { ...companyAccess };
      delete ca[org_id];
      setCompanyAccess(ca);
    } else {
      next.add(org_id);
    }
    setOrgAccess(next);
  };

  const toggleCompany = (org_id, company_name) => {
    if (!canGrantCompany(org_id, company_name)) {
      toast.info(`"${company_name}" is outside your scope.`);
      return;
    }
    const list = companyAccess[org_id] || [];
    const next = list.includes(company_name)
      ? list.filter((c) => c !== company_name)
      : [...list, company_name];
    const ca = { ...companyAccess };
    if (next.length === 0) delete ca[org_id];
    else ca[org_id] = next;
    setCompanyAccess(ca);
  };

  const save = async () => {
    try {
      setSaving(true);
      const res = await axios.put(
        `${API}/users/${user.user_id}/access`,
        { org_access: [...orgAccess], company_access: isMemberOrViewer ? companyAccess : {} },
        { withCredentials: true }
      );
      const body = res.data || {};
      if (body.partial) {
        const dropped = [
          ...(body.skipped?.orgs || []).map((o) => `org ${o}`),
          ...Object.entries(body.skipped?.companies || {}).flatMap(([o, cs]) => cs.map((c) => `${c} (in ${o})`)),
        ];
        toast.warning(`Access updated — but ${dropped.length} item(s) outside your scope were skipped: ${dropped.slice(0, 3).join(', ')}${dropped.length > 3 ? '…' : ''}`);
      } else {
        toast.success(`Access updated for ${user.name || user.email}`);
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="edit-access-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-500" />
            Access for {user.name || user.email}
            <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${ROLE_COLOR[user.role]}`}>
              {ROLE_LABEL[user.role] || user.role}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Manager-delegated mode banner */}
        {isDelegated && callerScope.delegatedEnabled && (
          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900" data-testid="delegated-banner">
            <strong>Delegated grant:</strong> you're editing as a Manager. You can only add access within your own scope (orgs and per-org companies). Any prior Admin grants are preserved automatically.
          </div>
        )}

        {/* Copy access from another user */}
        {copyCandidates.length > 0 && (
          <div className="flex items-center gap-2 px-1 py-1.5 -mt-1 mb-1 text-xs" data-testid="copy-access-row">
            <span className="text-gray-500">Quick action:</span>
            <select
              value={copySource}
              onChange={(e) => { if (e.target.value) copyFrom(e.target.value); }}
              className="flex-1 max-w-[280px] border border-gray-200 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-teal-400"
              data-testid="copy-access-select"
            >
              <option value="">Copy access from another user...</option>
              {copyCandidates.map((u) => {
                const orgCount = (u.org_access || []).length;
                const coCount = Object.values(u.company_access || {}).reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0);
                return (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name || u.email} · {u.role} · {orgCount} org{orgCount === 1 ? '' : 's'}{coCount > 0 ? ` · ${coCount} co. restr.` : ''}
                  </option>
                );
              })}
            </select>
            <span className="text-gray-400 text-[10px] hidden sm:inline">You can still tweak before saving</span>
          </div>
        )}

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {orgs.map((o) => {
            const hasOrg = orgAccess.has(o.org_id);
            const cos = companiesByOrg[o.org_id] || [];
            const restrictedList = companyAccess[o.org_id] || [];
            const locked = lockedOrgs.has(o.org_id);
            const outOfScope = !callerScope.unscoped && !callerOrgSet.has(o.org_id) && !locked;
            const muted = locked || outOfScope;
            return (
              <div
                key={o.org_id}
                className={`border rounded-lg p-3 ${
                  hasOrg ? 'border-teal-200 bg-teal-50/40' : 'border-gray-200'
                } ${muted ? 'opacity-60' : ''}`}
                data-testid={`access-org-${o.org_id}`}
              >
                <label className={`flex items-center gap-2 ${muted ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  title={
                    locked ? 'Granted by an Admin — outside your scope. Ask an Admin to revoke.' :
                    outOfScope ? 'Outside your scope — ask an Admin to grant access to this org first.' :
                    undefined
                  }
                >
                  <Checkbox
                    checked={hasOrg}
                    onCheckedChange={() => toggleOrg(o.org_id)}
                    disabled={muted}
                    data-testid={`access-org-checkbox-${o.org_id}`}
                  />
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span className="font-medium text-sm text-gray-900">{o.name}</span>
                  {locked && <span className="ml-1 text-[10px] uppercase font-semibold text-purple-600">Admin grant</span>}
                  {outOfScope && <span className="ml-1 text-[10px] uppercase font-semibold text-gray-400">Outside your scope</span>}
                </label>
                {hasOrg && isMemberOrViewer && cos.length > 0 && (
                  <div className="mt-2 pl-7">
                    <p className="text-[11px] text-gray-500 mb-1">
                      Companies in this org — leave all unchecked to grant access to <strong>all companies</strong>:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {cos.map((c) => {
                        const on = restrictedList.includes(c);
                        const grantable = canGrantCompany(o.org_id, c);
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => toggleCompany(o.org_id, c)}
                            disabled={!grantable && !on}
                            title={!grantable && !on ? 'Outside your scope' : undefined}
                            className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                              on
                                ? 'bg-teal-500 text-white border-teal-500'
                                : grantable
                                  ? 'bg-white text-gray-700 border-gray-300 hover:border-teal-300'
                                  : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                            }`}
                            data-testid={`access-company-${o.org_id}-${c}`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                    {restrictedList.length > 0 && (
                      <p className="text-[10px] text-teal-600 mt-1">
                        Restricted to {restrictedList.length} compan{restrictedList.length === 1 ? 'y' : 'ies'}.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="access-modal-cancel">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="bg-teal-500 hover:bg-teal-600" data-testid="access-modal-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
